import { isPdfPhoto } from '@/utils/photoFiles';
import { PdfPhotoTile } from './PdfPhotoTile';

/**
 * One cell of a photo grid, for grids whose contents may include a PDF.
 *
 * Since 2026-08 the photo uploads accept `application/pdf`, so every grid that
 * lists uploaded files can receive one. `accept="image/*"` does NOT prevent this
 * — it is a picker hint, and a desktop user can switch the dialog to "All
 * files" — so a grid that renders `<img>` unconditionally will show a broken
 * thumbnail and then feed the PDF into `PhotoLightbox`, an image zoom/pan
 * viewer that cannot display it.
 *
 * Callers must ALSO keep PDFs out of the array they hand to `PhotoLightbox`,
 * and derive `onOpen`'s index from that filtered array — otherwise next/prev
 * lands on an entry the viewer cannot render.
 */
export function PhotoGridThumb({
  photo,
  imgClassName,
  tileSizeClass,
  variant = 'grid',
  onOpen,
  openLabel,
}: {
  photo: { key: string; url: string; filename: string };
  /** Classes for the `<img>` (sizing, object-fit, per-item state such as opacity). */
  imgClassName: string;
  /** Box sizing for the PDF tile when it must match a non-default grid size. */
  tileSizeClass?: string;
  variant?: 'grid' | 'strip';
  onOpen: () => void;
  /** Accessible name of the image button; defaults to the Dutch "Foto … openen". */
  openLabel?: string;
}) {
  if (isPdfPhoto(photo)) {
    return <PdfPhotoTile url={photo.url} filename={photo.filename} variant={variant} sizeClass={tileSizeClass} />;
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      className="block cursor-pointer"
      aria-label={openLabel ?? `Foto ${photo.filename} openen`}
    >
      <img src={photo.url} alt={photo.filename} className={imgClassName} loading="lazy" />
    </button>
  );
}
